import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, Check, X, Clock, Calendar, Award, DollarSign, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatTimeAgo } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import notificationService from '@/services/notificationService';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [browserPermission, setBrowserPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );

  // Initialize browser notifications
  useEffect(() => {
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission);
      notificationService.registerServiceWorker();
    }
  }, []);

  // Request browser notification permission
  const requestBrowserPermission = async () => {
    const granted = await notificationService.requestPermission();
    setBrowserPermission(granted ? 'granted' : 'denied');
    if (granted) {
      toast.success('Browser notifications enabled! You will receive shift reminders.');
    } else {
      toast.error('Permission denied. Enable in browser settings.');
    }
  };

  // Check for shift reminders
  const checkShiftReminder = useCallback(async () => {
    if (browserPermission !== 'granted') return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get attendance config
      const configRes = await axios.get(`${API_BASE_URL}/hr/config`, { headers });
      const config = configRes.data;
      
      // Check if today is a working day
      const today = new Date().getDay();
      const dayIndex = today === 0 ? 6 : today - 1;
      if (!config.working_days?.includes(dayIndex)) return;
      
      // Get current user from token
      const meRes = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userId = meRes.data?.id;
      if (!userId) return;
      
      // Check if already marked attendance today
      const todayDate = new Date().toISOString().split('T')[0];
      const attendanceRes = await axios.get(
        `${API_BASE_URL}/hr/attendance/records?user_id=${userId}&start_date=${todayDate}&end_date=${todayDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (attendanceRes.data.records?.length > 0) return;
      
      // Check time
      const now = new Date();
      const [shiftHour, shiftMin] = config.shift_start_time.split(':').map(Number);
      const shiftStart = new Date();
      shiftStart.setHours(shiftHour, shiftMin, 0, 0);
      
      const minutesUntilShift = (shiftStart - now) / (1000 * 60);
      
      // Show reminder 30 minutes before shift
      if (minutesUntilShift > 0 && minutesUntilShift <= 30) {
        const lastReminder = localStorage.getItem('lastShiftReminder');
        if (lastReminder !== todayDate) {
          await notificationService.showShiftReminder(config.shift_start_time);
          localStorage.setItem('lastShiftReminder', todayDate);
        }
      }
      
      // Show late alert after grace period
      if (minutesUntilShift < -config.grace_period_minutes && minutesUntilShift > -config.late_threshold_minutes) {
        const lastLateAlert = localStorage.getItem('lastLateAlert');
        if (lastLateAlert !== todayDate) {
          await notificationService.showLateAttendanceAlert();
          localStorage.setItem('lastLateAlert', todayDate);
        }
      }
    } catch (error) {
      // Silently fail - HR config might not exist
    }
  }, [browserPermission]);

  useEffect(() => {
    loadNotifications();
    checkShiftReminder();
    const interval = setInterval(() => {
      loadNotifications();
      checkShiftReminder();
    }, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [checkShiftReminder]);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const notifs = Array.isArray(response.data) ? response.data : response.data.notifications || [];
      setNotifications(notifs.slice(0, 5)); // Show only 5 recent
      setUnreadCount(notifs.filter((n) => !n.is_read).length);
      // Express order sound alert — announce once per notification
      try {
        const seen = JSON.parse(localStorage.getItem('express_notif_seen') || '[]');
        const fresh = notifs.filter((n) => n.type === 'express_order' && !n.is_read && !seen.includes(n.id));
        if (fresh.length > 0 && window.speechSynthesis) {
          const u = new SpeechSynthesisUtterance('Express Order Received');
          u.rate = 0.9;
          window.speechSynthesis.speak(u);
          localStorage.setItem('express_notif_seen',
            JSON.stringify([...seen, ...fresh.map((n) => n.id)].slice(-100)));
        }
      } catch (e) { /* audio blocked until first interaction — fine */ }
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'attendance_reminder':
      case 'shift_reminder':
        return <Clock className="h-5 w-5 text-yellow-400" />;
      case 'leave_request':
      case 'leave_status':
        return <Calendar className="h-5 w-5 text-blue-400" />;
      case 'streak_milestone':
      case 'success':
        return <Award className="h-5 w-5 text-wt-gold" />;
      case 'salary_slip':
        return <DollarSign className="h-5 w-5 text-green-400" />;
      case 'alert':
        return <AlertTriangle className="h-5 w-5 text-red-400" />;
      default:
        return <Bell className="h-5 w-5 text-wt-text-muted" />;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-wt-subtle smooth-transition"
        data-testid="notifications-button"
      >
        {browserPermission === 'granted' ? (
          <Bell className="h-5 w-5 text-wt-text-secondary hover:text-wt-gold" />
        ) : (
          <BellOff className="h-5 w-5 text-wt-text-secondary hover:text-wt-gold" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
          <>
            <div
              className="fixed inset-0 z-[90] cursor-pointer"
              onClick={() => setIsOpen(false)}
              data-testid="notifications-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed right-2 sm:right-4 top-16 w-[min(24rem,calc(100vw-1rem))] bg-wt-paper border border-wt-border rounded-xl shadow-2xl z-[95] overflow-hidden"
              data-testid="notifications-dropdown"
            >
              <div className="p-4 border-b border-wt-border flex items-center justify-between">
                <h3 className="font-semibold text-wt-text-primary">Notifications</h3>
                {unreadCount > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={markAllAsRead}
                    className="text-xs text-wt-gold hover:text-wt-gold-hover"
                    data-testid="mark-all-read-btn"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Mark all read
                  </Button>
                )}
              </div>

              {/* Browser Permission Request */}
              {browserPermission !== 'granted' && (
                <div className="p-4 bg-wt-gold/10 border-b border-wt-border">
                  <div className="flex items-start gap-3">
                    <Bell className="h-5 w-5 text-wt-gold mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-wt-text-primary">Enable Push Notifications</p>
                      <p className="text-xs text-wt-text-muted mt-1">
                        Get shift reminders, leave updates & more
                      </p>
                      <Button
                        size="sm"
                        onClick={requestBrowserPermission}
                        className="mt-2 bg-wt-gold hover:bg-wt-gold-hover text-black text-xs"
                      >
                        Enable Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-wt-text-muted">
                    <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 border-b border-wt-border hover:bg-wt-subtle cursor-pointer smooth-transition ${
                        !notification.is_read ? 'bg-wt-gold/5' : ''
                      }`}
                      data-testid={`notification-${notification.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-1.5 rounded-full bg-wt-subtle">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-wt-text-primary text-sm mb-1">
                            {notification.title}
                          </p>
                          <p className="text-xs text-wt-text-secondary line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-wt-text-muted mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="h-2 w-2 rounded-full bg-wt-gold flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {notifications.length > 0 && (
                <div className="p-3 bg-wt-subtle text-center">
                  <button
                    onClick={() => {
                      navigate('/notifications');
                      setIsOpen(false);
                    }}
                    className="text-sm text-wt-gold hover:text-wt-gold-hover font-medium"
                  >
                    View All Notifications
                  </button>
                </div>
              )}
            </motion.div>
          </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default NotificationDropdown;